<template>
  <v-btn-toggle
    class="binary-toggle"
    :class="{ 'binary-toggle--compact': compact }"
    :id="id"
    :value="isOn ? 'on' : 'off'"
    :disabled="disabled"
    :aria-label="resolvedAriaLabel"
    @change="emitFor"
    mandatory
    dense
    color="primary">
    <v-btn value="off" small :disabled="disabled" :id="idFor('Off')">{{ offLabel }}</v-btn>
    <v-btn value="on" small :disabled="disabled" :id="idFor('On')">{{ onLabel }}</v-btn>
  </v-btn-toggle>
</template>

<script>
import '../../../assets/style.css';

// A segmented either/or control, for the places that used to carry a v-switch whose label was
// the name of one of two modes. A switch only ever showed the state it was in, so "Normal" or
// "Full Pay" read as the label of an unlabelled control: a clerk could not tell whether Normal
// was what they had or what clicking would give them. Both options stay on screen here, with
// the active one filled.
//
// offValue/onValue exist because not every one of these is a boolean - some write strings
// ('letter'/'legal') or a value whose name runs the opposite way to its label, as the import
// screens' isBackupNeeded does. The component holds no state of its own: it renders whichever
// segment matches `value` and emits the caller's own off/on value back, so the shape of what
// the caller stores is exactly what it was with the switch.
export default {
  name: 'BinaryToggle',
  props: {
    value: { type: [Boolean, String, Number], default: false },
    offLabel: { type: String, required: true },
    onLabel: { type: String, required: true },
    offValue: { type: [Boolean, String, Number], default: false },
    onValue: { type: [Boolean, String, Number], default: true },
    // Bound on the buttons as well as on the group: v-btn-toggle does not push it down,
    // so a group-level disabled alone still leaves both halves clickable.
    disabled: { type: Boolean, default: false },
    id: { type: String, default: undefined },
    ariaLabel: { type: String, default: '' },
    // Drops the equal-width minimum, for a criteria bar that cannot spare the pixels.
    compact: { type: Boolean, default: false },
  },
  computed: {
    // Strict: anything that is not onValue - including the undefined a criteria object has
    // before its first load - shows the off segment rather than an empty control.
    isOn() {
      return this.value === this.onValue;
    },
    resolvedAriaLabel() {
      return this.ariaLabel || `${this.offLabel} or ${this.onLabel}`;
    },
  },
  methods: {
    idFor(suffix) {
      return this.id ? `${this.id}${suffix}` : undefined;
    },
    emitFor(segment) {
      // `mandatory` stops Vuetify emitting null when the active segment is clicked again, but
      // treat anything that is not the on segment as off anyway: the caller has two values and
      // should never be handed a third.
      this.$emit('input', segment === 'on' ? this.onValue : this.offValue);
    },
  },
};
</script>
